<div class="flex flex-col w-full min-h-full bg-default">

    <!-- Screen 1: Partner Selection -->
    <div *ngIf="!selectedSkin" class="flex flex-col items-center justify-center min-h-screen p-8 bg-gray-50">
        <div class="max-w-5xl w-full">
            <h1 class="text-4xl font-extrabold text-center mb-10 text-gray-800">Select Partner</h1>
            <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                <mat-card *ngFor="let skin of skinList"
                          class="cursor-pointer hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
                          (click)="selectSkin(skin)">
                    <div class="p-6 flex flex-col items-center justify-center h-48 bg-white rounded-lg">
                        <img *ngIf="skin.mainLogo" [src]="skin.sideLogo || skin.mainLogo" [alt]="skin.name" class="h-16 object-contain mb-4">
                        <h3 class="text-xl font-bold text-gray-700">{{ skin.name }}</h3>
                        <div class="w-full h-2 mt-4 rounded-full" [style.background-color]="skin.themeColor"></div>
                    </div>
                </mat-card>
            </div>
        </div>
    </div>

    <!-- Screen 2: Main Generator -->
    <ng-container *ngIf="selectedSkin">

        <header class="h-16 flex items-center justify-start px-6 md:px-8 shadow-lg sticky top-0 z-40"
                [style.background-color]="selectedSkin.headerFooterColor">
            <div class="max-w-[1400px] w-full mx-auto flex justify-between items-center">
                <div class="flex items-center space-x-4">
                    <img *ngIf="selectedSkin.mainLogo" [src]="selectedSkin.mainLogo" alt="Main Logo" class="h-10 sm:h-12 object-contain">
                    <ng-container *ngIf="selectedSkin.sideLogo">
                        <div class="w-px h-8 bg-gray-400 opacity-50"></div>
                        <img [src]="selectedSkin.sideLogo" alt="Partner Logo" class="h-15 sm:h-15 object-contain">
                    </ng-container>
                </div>
                <button mat-stroked-button color="primary" (click)="backToSkins()">Switch Partner</button>
            </div>
        </header>

        <mat-progress-bar *ngIf="isLoading || isGenerating" mode="indeterminate" class="fixed top-16 left-0 right-0 z-50"></mat-progress-bar>

        <div class="flex flex-col w-full p-6 md:p-8 space-y-8 max-w-[1200px] mx-auto flex-grow">

            <h1 class="text-3xl font-extrabold text-default flex items-center gap-3">
                <mat-icon [style.color]="selectedSkin.themeColor">qr_code_2</mat-icon>
                <span [style.color]="selectedSkin.themeColor">AppDownload Link Generator</span>
            </h1>

            <!-- Search Section -->
            <mat-card class="p-6">
                <form [formGroup]="searchForm" (ngSubmit)="searchBusiness()">
                    <div class="flex gap-4 items-center">
                        <mat-form-field class="flex-auto" appearance="outline">
                            <mat-label>Business Name</mat-label>
                            <input matInput formControlName="ds_search" placeholder="Enter at least 3 characters">
                            <mat-icon matPrefix>search</mat-icon>
                            <button *ngIf="searchForm.get('ds_search')?.value" mat-icon-button matSuffix type="button" (click)="clearSearch()">
                                <mat-icon>close</mat-icon>
                            </button>
                        </mat-form-field>
                        <button mat-raised-button class="h-14 w-32 flex-shrink-0 text-white"
                                [style.background-color]="selectedSkin.themeColor"
                                [disabled]="searchForm.invalid || isLoading"
                                type="submit">
                            Search
                        </button>
                    </div>
                </form>

                <mat-nav-list *ngIf="searchResults.length > 0" class="mt-4 border rounded-lg overflow-hidden">
                    <mat-list-item *ngFor="let result of searchResults" (click)="selectBusiness(result)" class="hover:bg-gray-50 cursor-pointer">
                        <img matListItemAvatar [src]="result.im_mediacard || 'placeholder.png'" alt="Card">
                        <span matListItemTitle class="font-bold">{{ result.ds_name }}</span>
                        <span matListItemLine class="text-secondary">{{ result.ds_destinationtype }} in {{ result.ggparent?.ds_name || 'Global' }}</span>
                        <button mat-icon-button matListItemMeta>
                            <mat-icon>chevron_right</mat-icon>
                        </button>
                    </mat-list-item>
                </mat-nav-list>
            </mat-card>

            <!-- Details & QR Generation -->
            <mat-card *ngIf="selectedBusiness" class="p-6 mt-6">
                <div class="flex flex-col gap-6">
                    <!-- Business Info -->
                    <div class="flex items-start">
                        <img [src]="selectedBusiness.im_media || 'placeholder.png'" alt="Business Image" class="w-24 h-24 object-cover rounded-lg mr-6 shadow-sm">
                        <div>
                            <h2 class="text-2xl font-bold">{{ selectedBusiness.ds_name }}</h2>
                            <p class="text-sm text-secondary mb-2">ID: {{ selectedBusiness.id_destination }}</p>
                            <p class="text-default text-sm line-clamp-2" [innerHTML]="selectedBusiness.tx_description"></p>
                        </div>
                    </div>

                    <button mat-raised-button class="w-full !h-12 text-white text-lg"
                            [style.background-color]="selectedSkin.themeColor"
                            (click)="generateAdminLink()"
                            [disabled]="isGenerating || isLoading">
                        <mat-icon class="mr-2">link</mat-icon>
                        {{ isGenerating ? 'Generating...' : 'Generate Partner QR Links' }}
                    </button>

                    <mat-divider></mat-divider>

                    <!-- Results Section -->
                    <div class="min-h-[400px] flex items-center justify-center relative">
                        <ng-container *ngIf="generatedLinks; else noQrCode">
                            <mat-tab-group class="w-full" animationDuration="0ms">

                                <!-- Tab 1: Mobile -->
                                <mat-tab label="Mobile App">
                                    <div class="flex flex-col md:flex-row items-center justify-center gap-8 p-6">
                                        <div class="bg-white p-4 border rounded-xl shadow-sm">
                                            <ngx-qrcode-styling #mobileQrCode [config]="mobileQrConfig"></ngx-qrcode-styling>
                                        </div>
                                        <div class="flex flex-col gap-4 max-w-md">
                                            <h3 class="text-xl font-bold" [style.color]="selectedSkin.themeColor">Mobile App DeepLink</h3>
                                            <p class="text-sm text-gray-600">Scan to download the app configured for {{ selectedSkin.name }}.</p>

                                            <div class="flex gap-2">
                                                <button mat-stroked-button (click)="openZoomedQr(zoomMobile)" [style.color]="selectedSkin.themeColor">Zoom</button>
                                                <button mat-flat-button [style.background-color]="selectedSkin.themeColor" class="text-white" (click)="downloadQr('mobile')">Download PNG</button>
                                            </div>

                                            <div class="p-3 bg-gray-50 rounded border text-xs break-all cursor-pointer hover:bg-gray-100"
                                                 (click)="copyUrl(generatedLinks?.mobileUrl)" matTooltip="Copy URL">
                                                {{ generatedLinks?.mobileUrl }}
                                            </div>
                                        </div>
                                    </div>
                                </mat-tab>

                                <!-- Tab 2: Desktop -->
                                <mat-tab label="Desktop Web">
                                    <div class="flex flex-col md:flex-row items-center justify-center gap-8 p-6">
                                        <div class="bg-white p-4 border rounded-xl shadow-sm">
                                            <ngx-qrcode-styling #desktopQrCode [config]="desktopQrConfig"></ngx-qrcode-styling>
                                        </div>
                                        <div class="flex flex-col gap-4 max-w-md">
                                            <h3 class="text-xl font-bold" [style.color]="selectedSkin.themeColor">Web Sign-Up Link</h3>
                                            <p class="text-sm text-gray-600">Scan to go to the {{ selectedSkin.name }} registration page.</p>

                                            <div class="flex gap-2">
                                                <button mat-stroked-button (click)="openZoomedQr(zoomDesktop)" [style.color]="selectedSkin.themeColor">Zoom</button>
                                                <button mat-flat-button [style.background-color]="selectedSkin.themeColor" class="text-white" (click)="downloadQr('desktop')">Download PNG</button>
                                            </div>

                                            <div class="p-3 bg-gray-50 rounded border text-xs break-all cursor-pointer hover:bg-gray-100"
                                                 (click)="copyUrl(generatedLinks?.desktopUrl)" matTooltip="Copy URL">
                                                {{ generatedLinks?.desktopUrl }}
                                            </div>
                                        </div>
                                    </div>
                                </mat-tab>

                            </mat-tab-group>

                            <!-- Hidden QRs for Download -->
                            <div class="hidden-offscreen">
                                <ngx-qrcode-styling #bigMobileQrCode [config]="bigMobileQrConfig"></ngx-qrcode-styling>
                                <ngx-qrcode-styling #bigDesktopQrCode [config]="bigDesktopQrConfig"></ngx-qrcode-styling>
                            </div>
                        </ng-container>

                        <ng-template #noQrCode>
                            <div class="flex flex-col items-center justify-center p-8 text-center opacity-50">
                                <mat-icon class="icon-size-10 scale-150 mb-4">qr_code_Scanner</mat-icon>
                                <p>Select a business and click Generate to see QRs.</p>
                            </div>
                        </ng-template>
                    </div>
                </div>
            </mat-card>

        </div>

        <footer class="py-8 px-6 md:px-8 border-t" [style.background-color]="selectedSkin.headerFooterColor">
            <div class="max-w-[1400px] mx-auto opacity-90">
                <div class="grid grid-cols-[auto_1fr] items-center gap-4">
                    <img [src]="selectedSkin.mainLogo || 'assets/placeholder.png'" alt="Footer Logo" class="h-12 object-contain filter grayscale" />
                    <p class="flex-1 text-sm font-medium text-right text-gray-500">
                        Exclusive lifestyle & travel benefits.
                    </p>
                </div>
                <div class="h-px my-4 bg-gray-300"></div>
                <div class="text-xs text-center text-gray-400">
                    © 2025 Altitude Rewards, LLC. All Rights Reserved.
                </div>
            </div>
        </footer>
    </ng-container>
</div>

<!-- Modals -->
<ng-template #zoomMobile>
    <div class="bg-white p-6 rounded-xl flex flex-col items-center shadow-2xl">
        <h3 class="text-xl font-bold mb-4" [style.color]="selectedSkin?.themeColor">Mobile App QR</h3>
        <div class="responsive-qr-container">
            <ngx-qrcode-styling [config]="bigMobileQrConfig"></ngx-qrcode-styling>
        </div>
        <button mat-button (click)="_dialog.closeAll()" class="mt-4 text-red-500">Close</button>
    </div>
</ng-template>

<ng-template #zoomDesktop>
    <div class="bg-white p-6 rounded-xl flex flex-col items-center shadow-2xl">
        <h3 class="text-xl font-bold mb-4" [style.color]="selectedSkin?.themeColor">Desktop Web QR</h3>
        <div class="responsive-qr-container">
            <ngx-qrcode-styling [config]="bigDesktopQrConfig"></ngx-qrcode-styling>
        </div>
        <button mat-button (click)="_dialog.closeAll()" class="mt-4 text-red-500">Close</button>
    </div>
</ng-template>
